import { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';

import { FIRST_ACCESS_ROUTES } from '../../../pages/FirstAccess/routes';

import {
  INITIAL_PAYMENT_LINK_VALUES,
  IPaymentLinkFormValues,
  paymentLinkSchema,
} from './schema';
import { PaymentMethod } from './helpers/paymentMethods';
import { usePaymentLinkData } from './hooks/usePaymentLinkData';
import { usePaymentLinkSubmit } from './hooks/usePaymentLinkSubmit';


export const usePaymentLinkBootstrap = () => {
  const history = useHistory();
  const { link } = useParams<{ link: string }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const {
    isValidating,
    isExpired,
    isAlreadyPaid,
    paymentState,
    formValues,
  } = usePaymentLinkData({ link, reportError: setErrorMessage });

  const {
    isLoading,
    paymentSubmitted,
    paymentFailed,
    oneTimePaymentResult,
    submitPayment,
    dismissFailure,
    closeOneTimePayment,
    firstAccessEmail,
  } = usePaymentLinkSubmit({
    link,
    planId: paymentState.plan?.id,
    reportError: setErrorMessage,
  });

  const form = useFormik<IPaymentLinkFormValues>({
    initialValues: INITIAL_PAYMENT_LINK_VALUES,
    validationSchema: paymentLinkSchema,
    onSubmit: submitPayment,
  });

  const { setValues, setFieldValue } = form;
  useEffect(() => {
    if (formValues) setValues(formValues, false);
  }, [formValues, setValues]);

  const selectPaymentMethod = useCallback(
    (method: PaymentMethod) => setFieldValue('paymentMethodSelected', method),
    [setFieldValue],
  );

  const dismissError = useCallback(() => setErrorMessage(null), []);

  const goToLogin = useCallback(() => history.push('/auth/login'), [history]);

  const goToFirstAccess = useCallback(
    () =>
      history.push(FIRST_ACCESS_ROUTES.BASE, {
        email: firstAccessEmail,
        fromCheckout: true,
      }),
    [firstAccessEmail, history],
  );

  const accessPlatform = firstAccessEmail ? goToFirstAccess : goToLogin;
  const finishOneTimePayment = firstAccessEmail
    ? goToFirstAccess
    : closeOneTimePayment;

  return {
    form,
    paymentState,

    isValidating,
    isExpired,
    isAlreadyPaid,

    termsAccepted,
    setTermsAccepted,
    selectPaymentMethod,

    isLoading,
    paymentSubmitted,
    paymentFailed,
    oneTimePaymentResult,
    dismissFailure,
    finishOneTimePayment,

    errorMessage,
    dismissError,

    accessPlatform,
  };
};

export default usePaymentLinkBootstrap;
